/* eslint-disable max-lines */
import React from 'react';
import {
    HSChartDose,
    HSDrug,
    HSMedication,
    NimAvailableDrugDto
} from "server-openapi";
import styled from 'styled-components';
import { Order } from '../../../../common/Order';
import { Grid } from '../../../../kit/Grid';
import { Image } from '../../../../kit/Image';
import { Text } from '../../../../kit/Text';
import { AdministrationWarnings } from '../../../../syncstream/utils/DrugUtils';
import { OrderNotesProps } from '../../../FacilityManagement/FacilityOrdersPage/Components/OrderNotesDialog';
import { OrderResupplyProps } from '../../../FacilityManagement/FacilityOrdersPage/Components/OrderResupplyDialog';
import { PatchInfo } from '../MedicationListsTabbedRouter/TabLists/PatchesMedicationList';
import { PatchUtils } from '../patches/PatchUtils';
import { AdministrationWarningIcons, DrugLabel } from './DrugDetails';
import { getConsentDescription } from "../../../../core/utils/consentUtils";
import {DateUtils} from "../../../../core/utils/dateUtils";

export interface StatusLabelInfo {
  label: string;
  colour: string;
  backgroundColour: string;
}

export interface MedicationInfoSectionProps {
  facilityGroupId: number;
  medication?: HSMedication;
  drug?: HSDrug;
  route?: string;
  directions?: string;
  administrationWarnings: AdministrationWarnings;
  openOrderResupply?: (orderResupply: OrderResupplyProps) => void;
  openOrderNotes?: (orderNotes: OrderNotesProps) => void;
  findRecentOrder?: (hsPatientId: number, hsDrugCode: string) => Order | undefined;
  nimAvailableDrug?: NimAvailableDrugDto;
  patchInfo?: PatchInfo;
  chartItemDose?: HSChartDose;
  isTimeCritical?: boolean;
  isPsychotropicConsent?: boolean;
  statusLabel?: StatusLabelInfo;

  // Optional size constraints for the DrugImage
  drugImageMaxWidth?: string;   // e.g., '240px', '20rem', '30vw'
  drugImageMaxHeight?: string;  // e.g., '180px', '25vh'
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function MedicationInfoSection(props: MedicationInfoSectionProps) {
  const { patchInfo, drug, facilityGroupId, administrationWarnings, nimAvailableDrug, chartItemDose, isTimeCritical } =
    props;
  const route = props.route ?? props.nimAvailableDrug?.route;

  const consentDescription = getConsentDescription(props.medication?.psychotropicConsent, props.medication?.consent);

    return (
    <Grid cols={2} colsTemplate={'0.5fr 1fr'} gap={1}>
      <DrugInfoContainer>
        {props.statusLabel && (
          <StatusLabel
              style={{ color: props.statusLabel.colour, background: props.statusLabel.backgroundColour }}>
            {props.statusLabel.label}
          </StatusLabel>
        )}
        {drug?.imageUri && (
            <DrugImage
                src={drug.imageUri}
                facilityGroupId={facilityGroupId}
                $maxWidth={props.drugImageMaxWidth}
                $maxHeight={props.drugImageMaxHeight}
            />
        )}
      </DrugInfoContainer>

      <DrugInfoContainer>
        {drug && (
          <DrugLabel drug={drug} isCeased={administrationWarnings.isCeasedMedication} isTimeCritical={isTimeCritical} isPsychotropicConsent={props.isPsychotropicConsent!} />
        )}
        <Text>{drug?.genericName && `(${drug.genericName})`}</Text>
        {nimAvailableDrug ? (
          <>
            {(nimAvailableDrug.dose?.length ?? 0) > 0 || (nimAvailableDrug.frequency?.length ?? 0) > 0? (
              <Text>
                {nimAvailableDrug.dose} {nimAvailableDrug.frequency}
              </Text>
            ) : null}
            <Text>
              {`Indications: ${nimAvailableDrug.indications} ${nimAvailableDrug.route}`}
            </Text>
          </>
        ) : (
          <Text>
            {props.directions} {route && `[${route}]`}
          </Text>
        )}
        {patchInfo && patchInfo.PatchStatus ? (
          <PatchInfoSection patchInfo={patchInfo} drug={drug} />
        ) : (
          <>
            {chartItemDose?.reasonForPrescribing && (
              <Text>Reason for prescribing: {chartItemDose?.reasonForPrescribing}</Text>
            )}
            {chartItemDose?.indications && <Text>Indications: {chartItemDose?.indications}</Text>}
            {consentDescription && <Text>{consentDescription}</Text>}
            <WarningContainer>
              <AdministrationWarningIcons administrationWarnings={administrationWarnings} />
            </WarningContainer>
          </>
        )}
      </DrugInfoContainer>
    </Grid>
  );
}

function PatchInfoSection(props: { patchInfo: PatchInfo; drug?: HSDrug }) {
  const { patchInfo, drug } = props;

  const isPatchRemoval = PatchUtils.isPatchRemoval(drug!);

  if (isPatchRemoval) {
    return <></>;
  }

  return (
    <> 
      {!!patchInfo.PatchApplication ? <>
            {patchInfo.PatchApplication.scheduledEnd && <Text>Patch time remaining: {DateUtils.intervalAsString(new Date(), patchInfo.PatchApplication.scheduledEnd)}</Text>}
            <Text>
              {patchInfo.ActionedDate && <>{DateUtils.toDisplayString(patchInfo.PatchApplication.clientUpdatedAt)}</>}
              {patchInfo.ActionedBy && <> by {patchInfo.PatchApplication.clientUser?.firstName} {patchInfo.PatchApplication.clientUser?.familyName}</>}
            </Text>
        
      </> :
          <>
            {patchInfo.TimeRemaining && <Text>Patch time remaining: {patchInfo.TimeRemaining}</Text>}
            <Text>
              {patchInfo.ActionedDate && <>{patchInfo.ActionedDate}</>}
              {patchInfo.ActionedBy && <> by {patchInfo.ActionedBy}</>}
            </Text>
          </>
      }
    </>
  );
}

const DrugImage = styled(Image)<{
    // Optional, only applied when provided. Accepts any valid CSS length (e.g., '240px', '15rem', '30vw').
    $maxWidth?: string;
    $maxHeight?: string;
}>`

    object-fit: cover;
    
    /* Responsive by default: fills the container width, keeps aspect ratio,
       and respects optional max size constraints if provided. */
    width: 100%;
    height: auto;

    /* Only constrain if props are provided; otherwise, behave as before */
    max-width: ${({ $maxWidth }) => $maxWidth ?? '100%'};
    max-height: ${({ $maxHeight }) => $maxHeight ?? 'none'};

    border-radius: 3px;
`;

const DrugInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  word-wrap: break-word;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const WarningContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.2em;
  padding: 0.2em;
`;

const StatusLabel = styled.div`
  text-align: center;
  padding: 0.2em;
  margin-bottom: 0.5em;
`;
